import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import TagManager from 'react-gtm-module';

// --- INICIALIZACIÓN GTM ---
const tagManagerArgs = {
  gtmId: 'GTM-XXXXXXX' // Pon aquí tu ID real
};
TagManager.initialize(tagManagerArgs);

// --- CONTEXTOS Y SEGURIDAD ---
import { AuthProvider } from './context/AuthProvider';
import AuthModal from './components/auth/AuthModal'; 
import ProtectedRoute from './components/auth/ProtectedRoute';

// --- LAYOUTS ---
import PublicLayout from './components/layouts/PublicLayout';
import AdminLayout from './components/layouts/AdminLayout';

// --- PÁGINAS PÚBLICAS ---
import LandingPage from './pages/public/LandingPage';
import TrackingPage from './pages/public/TrackingPage';
import CotizadorPage from './pages/public/CotizadorPage'; 
import TerminosPage from './pages/public/TerminosPage';
import PrivacidadPage from './pages/public/PrivacidadPage';
import FAQPage from './pages/public/FAQPage';
import ProfilePage from './pages/public/ProfilePage';
import ReviewsPage from './pages/public/ReviewsPage';
import BolsaTrabajo from './pages/public/BolsaTrabajo';
import PaymentView from './pages/public/PaymentView';
import PuntoEntregaPage from './pages/public/PuntoEntregaPage';
import NosotrosPage from './pages/public/NosotrosPage';

// --- PÁGINAS ADMINISTRATIVAS ---
import PageTransition from './components/PageTransition';
import DashboardPage from './pages/admin/DashboardPage';
import ShipmentsPage from './pages/admin/ShipmentsPage';
import RutasPage from './pages/admin/RutasPage'; 
import UsuariosPage from './pages/admin/UsuariosPage';
import DriversPage from './pages/admin/DriversPage';
import AdminReviewsPage from './pages/admin/AdminReviewsPage';
import AdminJobsPage from './pages/admin/AdminJobsPage';

/**
 * AnimatedRoutes: Controla la lógica de navegación y animaciones.
 * Es crucial extraer esto a un componente hijo para poder usar `useLocation`
 * dentro del alcance (scope) del <BrowserRouter>.
 */
function AnimatedRoutes() {
  // 1. Extraemos location para las animaciones de salida/entrada
  const location = useLocation();

  return (
    <>
      {/* 2. Le pasamos el location y un key único al Routes */}
      <AnimatePresence mode="wait">
        <Routes location={location} key={location.pathname}>
          
          {/* ==========================================
              RUTAS PÚBLICAS
          ========================================== */}
          <Route element={<PublicLayout />}>
            <Route index element={<PageTransition><LandingPage /></PageTransition>} />
            <Route path="/rastreo" element={<PageTransition><TrackingPage /></PageTransition>} />
            <Route path="/cotizar" element={<PageTransition><CotizadorPage /></PageTransition>} />
            <Route path="/nosotros" element={<PageTransition><NosotrosPage /></PageTransition>} />
            <Route path="/terminos" element={<PageTransition><TerminosPage /></PageTransition>} />
            <Route path="/privacidad" element={<PageTransition><PrivacidadPage /></PageTransition>} />
            <Route path="/preguntas" element={<PageTransition><FAQPage /></PageTransition>} />
            <Route path="/profile" element={<PageTransition><ProfilePage /></PageTransition>} />
            <Route path="/reviews" element={<PageTransition><ReviewsPage /></PageTransition>} />
            <Route path="/trabajos" element={<PageTransition><BolsaTrabajo /></PageTransition>} />
            <Route path="/Pagos" element={<PageTransition><PaymentView /></PageTransition>} />
            <Route path="/punto-entrega" element={<PageTransition><PuntoEntregaPage /></PageTransition>} />
          </Route>

          {/* ==========================================
              RUTAS PRIVADAS DEL PANEL ADMINISTRATIVO
          ========================================== */}
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<Navigate to="/admin/dashboard" replace />} />
            
            <Route path="dashboard" element={
              <ProtectedRoute>
                <PageTransition><DashboardPage /></PageTransition>
              </ProtectedRoute>
            } />

            <Route path="envios" element={    
              <ProtectedRoute>
                <PageTransition><ShipmentsPage /></PageTransition>
              </ProtectedRoute>
            } />

            <Route path="rutas" element={
              <ProtectedRoute requiredPermission="rutas_view">
                <PageTransition><RutasPage /></PageTransition>
              </ProtectedRoute>
            } />

            <Route path="usuarios" element={
              <ProtectedRoute requiredPermission="usuarios_view">
                <UsuariosPage />
              </ProtectedRoute>
            } />
            
            <Route path="choferes" element={
              <ProtectedRoute requiredPermission="choferes_view">
                <PageTransition><DriversPage /></PageTransition>
              </ProtectedRoute>
            } />

            <Route path="reseñas" element={
              <ProtectedRoute requiredPermission="reseñas_view">
                <PageTransition><AdminReviewsPage /></PageTransition>
              </ProtectedRoute>
            } />

            <Route path="trabajo" element={
              <ProtectedRoute requiredPermission="vacantes_view">
                <PageTransition><AdminJobsPage /></PageTransition>
              </ProtectedRoute>
            } />
          </Route>
          
          {/* Fallback para rutas no encontradas */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AnimatePresence>
    </>
  );
}

/**
 * App: Componente Principal.
 * El orden es CRÍTICO: 
 * 1. BrowserRouter (GPS y capa de navegación global)
 * 2. AuthProvider (Gestor de sesiones que usa useNavigate)
 * 3. AnimatedRoutes (Renderizado de páginas en base a la URL)
 * 4. AuthModal (Ventana de login sobre toda la aplicación)
 */
export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AnimatedRoutes />
        <AuthModal />
      </AuthProvider>
    </BrowserRouter>
  );
}